import { createPortraitStore } from "./portrait-source.js";
import { portraitBox, portraitName, drawPortrait } from "./portrait-rendering.js";
import { labelInk } from "./atlas-color.js";
import { createLabelIndex } from "./label-spatial-index.js";
import { smoothstep as revealStep } from "./semantic-zoom.js";
import { installPathCurves } from "./path-curves.js";
import { drawPathEdgeLabels } from "./path-edge-labels.js";
import { rankImportance, labelAppearance, approachOpacity } from "./label-importance.js";

export function intersects(a, b, margin = 0) {
  return a.left < b.right + margin && a.right > b.left - margin && a.top < b.bottom + margin && a.bottom > b.top - margin;
}

// CSS-pixel boxes, bounded typography, stable weighted ordering and a retiring
// label reservation pass keep the reveal soft without ever overlapping text.
export function installGraphLabels(renderer, graph, semantic) {
  const mobile = matchMedia("(max-width: 760px)"), reduced = matchMedia("(prefers-reduced-motion: reduce)");
  const context = renderer.getCanvases().labels.getContext("2d");
  const portraitCanvas=document.createElement("canvas");portraitCanvas.className="atlas-portraits";portraitCanvas.setAttribute("aria-hidden","true");
  // Above hover-node WebGL too: no selection marker may cover a person's face.
  renderer.getContainer().append(portraitCanvas);
  const portraitContext=portraitCanvas.getContext("2d");
  // The same tracking is used for measuring, collision and painting.
  const setType = (font,ctx=context) => { ctx.font = font; ctx.fontKerning = "normal"; ctx.letterSpacing = ".15px"; };
  const spatial = createLabelIndex(renderer, graph);
  const curveLayer = installPathCurves(renderer, graph);
  let communities = null;
  let paths = null, spotlight = null, previousEdges = new Map();
  let hovered = null, dirty = true, importance = new Map(), previous = new Map();
  let overviewRatio = 1, lastTime = performance.now(), frame = null, killed = false;
  const widths = new Map();
  const portraits = createPortraitStore(qid => { renderer.scheduleRender(); audit.onPortraitChange?.(qid); }, {
    limit:mobile.matches?48:96, byteLimit:(mobile.matches?4:8)*1024*1024,
  });
  const audit = { reset(){previous.clear();previousEdges.clear();hovered=null;portraitCanvas.width=portraitCanvas.width;renderer.scheduleRender();}, portraits, inspectorPeople:null, onPortraitChange:null, setCommunities(next) { communities = next; dirty = true; }, passes: 0, boxes: [], edgeBoxes: [], curves: [], setPaths(next) { paths = next; previous.clear(); previousEdges.clear(); renderer.scheduleRender(); }, setSpotlight(next) { spotlight = next; renderer.scheduleRender(); }, setOverviewRatio(ratio) { overviewRatio = ratio; } };
  const invalidate = () => { dirty = true; };
  const topologyEvents = ["nodeAdded", "nodeDropped", "edgeAdded", "edgeDropped", "cleared", "edgesCleared"];
  topologyEvents.forEach(event => graph.on(event, invalidate));
  renderer.setSetting("renderLabels", false);
  renderer.setSetting("defaultDrawNodeHover", (ctx, data) => {
    if (semantic.style().portraitMix > .5 && !paths) return;
    ctx.fillStyle = data.color; ctx.beginPath(); ctx.arc(data.x, data.y, data.size + 2, 0, 2 * Math.PI); ctx.fill();
  });
  renderer.on("enterNode", ({ node }) => { hovered = node; renderer.scheduleRender(); });
  renderer.on("leaveNode", () => { hovered = null; renderer.scheduleRender(); });
  function textWidth(font, text) {
    const key = font + text;
    if (!widths.has(key)) { if (widths.size > 10000) widths.clear(); widths.set(key, context.measureText(text).width); }
    return widths.get(key);
  }
  function shorten(label, font, limit) {
    if (textWidth(font, label) <= limit) return label;
    const characters = [...label]; let low = 0, high = characters.length;
    while (low < high) {
      const mid = Math.ceil((low + high) / 2);
      if (textWidth(font, characters.slice(0, mid).join("") + "…") <= limit) low = mid; else high = mid - 1;
    }
    return characters.slice(0, low).join("") + "…";
  }
  renderer.on("afterRender", () => {
    if (killed) return;
    if (dirty) { importance = rankImportance(graph, communities); dirty = false; }
    const now = performance.now(), elapsed = Math.max(1, now - lastTime); lastTime = now;
    const { width, height } = renderer.getDimensions(), viewport = renderer.getContainer().getBoundingClientRect();
    const z = semantic.value(), zoom = 2 ** z;
    const lod=semantic.style(),portraitMix = paths ? 0 : lod.portraitMix;
    const obstacles = [".topbar", ".mobile-graph-tools", "#inspector", ".toolrail", "#pathNotice", "#neighbourhoodControls", "#mapControls"].flatMap(selector => {
      const element = document.querySelector(selector);
      if (!element || element.hidden) return [];
      const r = element.getBoundingClientRect();
      return r.width && r.height ? [{ left: r.left - viewport.left, right: r.right - viewport.left,
        top: r.top - viewport.top, bottom: r.bottom - viewport.top }] : [];
    });
    context.clearRect(0, 0, width, height);
    const dpr=window.devicePixelRatio||1,pw=Math.round(width*dpr),ph=Math.round(height*dpr);
    if(portraitCanvas.width!==pw||portraitCanvas.height!==ph){portraitCanvas.width=pw;portraitCanvas.height=ph;portraitCanvas.style.width=width+"px";portraitCanvas.style.height=height+"px";}
    portraitContext.setTransform(dpr,0,0,dpr,0,0);portraitContext.clearRect(0,0,width,height);
    audit.curves = curveLayer.draw(paths, mobile.matches);
    const nodes = [...spatial.query(width, height)].map(id => {
      if (paths && !paths.ids.includes(id) && !paths.nodes.has(id)) return null;
      const data = renderer.getNodeDisplayData(id);
      const priority = paths ? paths.ids.includes(id) || data.highlighted || id === hovered : spotlight?.has(id) || id === hovered || data.highlighted;
      const rank = importance.get(id);
      if (!priority && !data.atlasNeighborhood && !paths && z < (rank?.minLabelZoom ?? 0) - lod.portraitPreviewLead - (previous.has(id) ? .22 : 0)) return null;
      const point = renderer.framedGraphToViewport(data);
      return { id, ...data, ...point, ...importance.get(id), size: renderer.scaleSize(data.size), priority: paths ? paths.ids.includes(id) || data.highlighted || id === hovered : spotlight?.has(id) || id === hovered || data.highlighted };
    }).filter(a => a && a.label && !a.hidden && a.x >= 0 && a.x <= width && a.y >= 0 && a.y <= height);
    // Previous membership breaks equal-degree ties only; it cannot outrank a
    // better-connected person. Local rank makes a panned neighborhood explorable.
    nodes.sort((a, b) => Number(Boolean(b.highlighted)) - Number(Boolean(a.highlighted)) || Number(b.priority) - Number(a.priority) || Number(Boolean(b.atlasNeighborhood)) - Number(Boolean(a.atlasNeighborhood)) || Number(Boolean(b.landmark)) - Number(Boolean(a.landmark)) || b.degree - a.degree ||
      Number(previous.has(b.id)) - Number(previous.has(a.id)) || a.id.localeCompare(b.id));
    const desired = new Map(), candidates = new Map(), cells = new Map();
    const keys = (box, margin = 0) => {
      const result = [];
      for (let x = Math.floor((box.left - margin) / 100); x <= Math.floor((box.right + margin) / 100); x++)
        for (let y = Math.floor((box.top - margin) / 40); y <= Math.floor((box.bottom + margin) / 40); y++) result.push(`${x}:${y}`);
      return result;
    };
    const collides = (box, margin = 0) => keys(box, margin).some(key => (cells.get(key) || []).some(other => intersects(box, other, margin)));
    const reserve = box => keys(box).forEach(key => { if (!cells.has(key)) cells.set(key, []); cells.get(key).push(box); });
    if (paths) nodes.forEach(node => obstacles.push({ left: node.x - node.size - 2, right: node.x + node.size + 2,
      top: node.y - node.size - 2, bottom: node.y + node.size + 2 }));
    let localRank = 0;
    for (const node of nodes) {
      if (portraitMix > 0 && desired.size >= lod.portraitLimit && !node.priority) continue;
      const style = paths ? { fontSize: node.priority ? (mobile.matches ? 17 : 20) : (mobile.matches ? 13 : 12), weight: node.priority ? 600 : 500, opacity: 1 }
        : spotlight?.has(node.id) ? { fontSize: mobile.matches ? 12 : 14, weight: 600, opacity: 1 }
        : labelAppearance({ ...node, localRank: localRank++, candidateCount: nodes.length, zoom, mobile: mobile.matches, labelSize: renderer.getSetting("labelSize") });
      const emergence = node.priority || node.atlasNeighborhood || paths ? 1 : revealStep(node.minLabelZoom-lod.portraitPreviewLead,node.minLabelZoom+.6,z);
      if (!node.priority && !node.atlasNeighborhood && !paths) style.opacity *= revealStep(node.minLabelZoom-lod.portraitPreviewLead-(previous.has(node.id) ? .22 : 0),node.minLabelZoom+.25,z);
      if (portraitMix > 0) style.fontSize = Math.max(mobile.matches?14:13,Math.min(lod.portraitFontMax,style.fontSize));
      if (style.opacity < .008 && !previous.has(node.id)) continue;
      const font = `${style.weight} ${style.fontSize}px ${renderer.getSetting("labelFont")}`;
      setType(font);
      // Narrow screens need earlier wrapping, not smaller type or displacement
      // of the underlying person. Desktop retains its broader name measure.
      const nameLimit=Math.min(mobile.matches?170:200,width*(mobile.matches?.46:.62),2*Math.min(node.x-5,width-node.x-5));
      const lines = portraitMix > 0 ? portraitName(node.label,text=>textWidth(font,text),Math.max(1,nameLimit),previous.get(node.id)?.lines) : null;
      const text = lines ? lines.map(l=>l.text).join(" ") : shorten(paths && paths.ids.includes(node.id) ? String.fromCharCode(65 + paths.ids.indexOf(node.id)) + " · " + node.label : node.label, font, Math.min(mobile.matches ? 240 : 320, width * .62));
      const length = lines ? Math.max(...lines.map(l=>l.width)) : textWidth(font, text), gap = Math.min(node.size, 12) + 5;
      const positions = [
        { x: node.x + gap, y: node.y + style.fontSize / 3, placement: 0 },
        { x: node.x - gap - length, y: node.y + style.fontSize / 3, placement: 1 },
        { x: node.x - length / 2, y: node.y - gap - 4, placement: 2 },
        { x: node.x - length / 2, y: node.y + gap + style.fontSize, placement: 3 },
      ];
      if (paths) {
        for (let offset = 24; offset < height; offset += 24) for (const sign of [-1, 1]) {
          positions.push({ x: Math.max(5, Math.min(width - length - 5, node.x + gap)), y: node.y + sign * offset,
            placement: positions.length, leader: true, anchorX: node.x, anchorY: node.y });
        }
      }
      const oldPlacement = previous.get(node.id)?.placement;
      if (oldPlacement !== undefined) positions.sort((a, b) => Number(b.placement === oldPlacement) - Number(a.placement === oldPlacement));
      let options = positions.map(position => ({ ...node, ...style, ...position, text, font,
        left: position.x - 4, right: position.x + length + 4, top: position.y - style.fontSize - 3, bottom: position.y + 5,
      }));
      if (portraitMix > 0) options = [portraitBox({node,text,length,font,...style,mix:portraitMix,mobile:mobile.matches,start:positions[0],lines,
        size:lod.portraitSize*(.3+.7*emergence),gap:lod.portraitGap,textOpacity:node.priority||node.atlasNeighborhood?1:revealStep(node.minLabelZoom-.05,node.minLabelZoom+.4,z)})];
      options = options.filter(box => box.left >= 0 && box.right <= width && box.top >= 0 && box.bottom <= height && !obstacles.some(other => intersects(box, other, 3)));
      if (options.length) candidates.set(node.id, options[0]);
      if (style.opacity < .008) continue;
      // Portrait unions already include a 5px halo/touch reservation on each
      // side. Adding the old text-only margin here needlessly hides landmarks.
      const box = options.find(box => !collides(box, portraitMix || previous.has(node.id) ? 0 : mobile.matches ? 6 : 3));
      if (box) { desired.set(node.id, box); reserve(box); }
    }
    // Fade retiring labels before admitting conflicting new ones. They follow
    // current node positions, not stale canvas coordinates during a pan.
    cells.clear();
    const visible = new Map(); let animating = false;
    for (const [id, old] of previous) {
      if (desired.has(id)) continue;
      const box = candidates.get(id);
      const alpha = approachOpacity(old.alpha, 0, elapsed, reduced.matches);
      if (!box || alpha < .008 || collides(box) || [...desired.values()].some(next => next.priority && intersects(box, next))) continue;
      visible.set(id, { ...box, alpha }); reserve(box); animating = true;
    }
    for (const [id, box] of desired) {
      if (collides(box)) { animating = true; continue; }
      const alpha = paths || box.priority ? box.opacity : approachOpacity(previous.get(id)?.alpha || 0, box.opacity, elapsed, reduced.matches);
      if (Math.abs(alpha - box.opacity) > .008) animating = true;
      visible.set(id, { ...box, alpha }); reserve(box);
    }
    const wanted=[...visible.values()].filter(box=>box.portrait&&box.alpha>.2&&box.mix>.1).map(box=>graph.getNodeAttributes(box.id));
    // A visible person/relation inspector may occlude its graph marks. Its one
    // or two people have explicit priority, within the same memory/request caps.
    wanted.unshift(...(audit.inspectorPeople?.() || []).slice(0,2));
    portraits.request(wanted);
    for (const box of visible.values()) {
      const paint=box.portrait?portraitContext:context;
      paint.globalAlpha = box.alpha; setType(box.font,paint);
      if (box.portrait) drawPortrait(paint,box,portraits.get(graph.getNodeAttribute(box.id,"wikidataId")),box.filterDomain ? box.color : "#81978a");
      if (box.leader) {
        paint.strokeStyle = "#67736f"; paint.lineWidth = .6;
        paint.beginPath(); paint.moveTo(box.anchorX, box.anchorY); paint.lineTo(box.left, box.bottom - 6); paint.stroke();
      }
      // Halo rather than rectangular label furniture, including selected people.
      paint.strokeStyle = "rgba(8,14,16,.88)"; paint.lineWidth = box.priority ? 4 : 3.5; paint.lineJoin = "round";
      paint.fillStyle = labelInk(box.filterDomain ? box.color : null, box.priority);
      paint.globalAlpha=box.alpha*(box.textOpacity??1);
      (box.lines || [{text:box.text,width:box.textWidth}]).forEach((line,index)=>{
        const x=box.x+(box.lines?(box.textWidth-line.width)/2:0),y=box.y+index*box.fontSize*1.25;
        paint.strokeText(line.text,x,y);paint.fillText(line.text,x,y);
      });
    }
    context.globalAlpha = 1;
    audit.edgeBoxes = drawPathEdgeLabels({ context, graph, renderer, paths, mobile: mobile.matches, width, height,
      curves: new Map(audit.curves.map(curve => [curve.id, curve])), collides, reserve, obstacles, intersects, previous: previousEdges });
    previousEdges = new Map(audit.edgeBoxes.map(box => [box.id, box]));
    previous = visible; audit.boxes = [...visible.values()].map(({ id, left, right, top, bottom, fontSize, alpha, degree, importance, text, textOpacity, portrait, photoX, photoY, photoSize }) =>
      ({ id, left, right, top, bottom, fontSize, alpha, degree, importance, text, textOpacity, portrait, photoX, photoY, photoSize,
        portraitStatus:portrait ? portraits.get(graph.getNodeAttribute(id,"wikidataId"))?.status || "unrequested" : undefined }));
    audit.zoom = zoom; audit.passes++;
    if (animating && frame === null) frame = requestAnimationFrame(() => { frame = null; if (!killed) renderer.scheduleRender(); });
  });
  renderer.on("kill", () => { portraitCanvas.remove(); portraits.kill(); spatial.kill(); curveLayer.kill(); killed = true; if (frame !== null) cancelAnimationFrame(frame); topologyEvents.forEach(event => graph.off(event, invalidate)); });
  document.fonts?.ready.then(() => { widths.clear(); if (!killed) renderer.scheduleRender(); });
  return audit;
}
